import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSocket } from '../hooks/useSocket';
import { Loader2, CheckCircle2, AlertCircle, RefreshCw, Banknote } from 'lucide-react';
import { API_BASE_URL } from './config/api';

interface PreloadEvent {
  order_id: number;
  status: 'sending' | 'success' | 'failed' | 'switched_to_cash';
  reason?: string;
  message?: string;
}

interface Props {
  storeId: number;
  orderId: number;
  /** Initial status from order.delivery_payment_status — drives the banner before
   *  any WS event arrives (e.g. when a failed order was already stored). */
  initialDeliveryPaymentStatus?: string | null;
  /** Called when the cashier confirms switch-to-cash so the parent list can
   *  refresh that single row without a full re-fetch. */
  onChanged?: () => void;
}

const SUCCESS_AUTO_DISMISS_MS = 4000;

const OrderPreloadBanner: React.FC<Props> = ({ storeId, orderId, initialDeliveryPaymentStatus, onChanged }) => {
  const { t } = useTranslation('orders');
  const { socket } = useSocket();
  const [evt, setEvt] = useState<PreloadEvent | null>(() => {
    // Seed from the initial order status so the banner is visible immediately
    // for orders that were already in 'processing' or 'failed' on page load.
    if (initialDeliveryPaymentStatus === 'processing') return { order_id: orderId, status: 'sending' };
    if (initialDeliveryPaymentStatus === 'failed') return { order_id: orderId, status: 'failed' };
    return null;
  });
  const [confirmCash, setConfirmCash] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!socket) return;
    const handler = (payload: PreloadEvent) => {
      if (payload?.order_id !== orderId) return;
      setEvt(payload);
      if (payload.status === 'success' || payload.status === 'switched_to_cash') {
        if (dismissTimer.current) clearTimeout(dismissTimer.current);
        dismissTimer.current = setTimeout(() => setEvt(null), SUCCESS_AUTO_DISMISS_MS);
      }
    };
    socket.on('pos_preload:update', handler);
    return () => {
      socket.off('pos_preload:update', handler);
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
    };
  }, [socket, orderId]);

  const callApi = async (path: string) => {
    const r = await fetch(`${API_BASE_URL}/api/delivery/${storeId}/orders/${orderId}/${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
      },
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.message || err.error || `HTTP ${r.status}`);
    }
    return r.json();
  };

  const handleRetry = async () => {
    setSubmitting(true);
    try {
      await callApi('preload-retry');
      setEvt({ order_id: orderId, status: 'sending' });
    } catch (err: any) {
      alert(err.message || t('preloadBanner.retryFailed', { defaultValue: 'Η νέα δοκιμή απέτυχε' }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSwitchToCash = async () => {
    setSubmitting(true);
    try {
      await callApi('switch-to-cash');
      setConfirmCash(false);
      onChanged?.();
    } catch (err: any) {
      alert(err.message || t('preloadBanner.switchFailed', { defaultValue: 'Η αλλαγή σε μετρητά απέτυχε' }));
    } finally {
      setSubmitting(false);
    }
  };

  if (!evt) return null;

  // Visual classes per status
  const wrap = (variant: 'info' | 'warn' | 'ok' | 'err' | 'cash') => {
    const map = {
      info: 'bg-blue-50 dark:bg-blue-900/30 border-blue-500/40 text-blue-800 dark:text-blue-200',
      warn: 'bg-amber-50 dark:bg-amber-900/30 border-amber-500/40 text-amber-900 dark:text-amber-200',
      ok: 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-500/40 text-emerald-800 dark:text-emerald-200',
      err: 'bg-red-50 dark:bg-red-900/30 border-red-500/40 text-red-800 dark:text-red-200',
      cash: 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-500/40 text-emerald-800 dark:text-emerald-200',
    };
    return `flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium ${map[variant]}`;
  };

  return (
    <>
      {evt.status === 'sending' && (
        <div className={wrap('info')}>
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>{t('preloadBanner.sending', { defaultValue: 'Αποστολή στο POS τερματικό…' })}</span>
        </div>
      )}

      {evt.status === 'success' && (
        <div className={wrap('ok')}>
          <CheckCircle2 className="h-4 w-4" />
          <span>{t('preloadBanner.success', { defaultValue: 'POS έτοιμο, παρέδωσε το τερματικό στον πελάτη' })}</span>
        </div>
      )}

      {evt.status === 'switched_to_cash' && (
        <div className={wrap('cash')}>
          <Banknote className="h-4 w-4" />
          <span>{t('preloadBanner.switchedToCash', { defaultValue: 'Η παραγγελία θα πληρωθεί με μετρητά' })}</span>
        </div>
      )}

      {evt.status === 'failed' && (
        <div className={wrap(evt.reason === 'busy' ? 'warn' : 'err')}>
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="flex-1">
            {evt.reason === 'busy'
              ? t('preloadBanner.busy', {
                  defaultValue: 'Το POS είναι απασχολημένο. Πάτησε «Νέα δοκιμή» όταν είσαι έτοιμος.',
                })
              : t('preloadBanner.failed', { defaultValue: 'Το POS δεν δέχτηκε τη συναλλαγή' })}
          </span>
          <button
            onClick={handleRetry}
            disabled={submitting}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-t1 text-xs font-semibold transition-all active:scale-95 disabled:opacity-50 ${
              evt.reason === 'busy'
                ? 'bg-amber-700/40 hover:bg-amber-700/60'
                : 'bg-red-700/40 hover:bg-red-700/60'
            }`}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${submitting ? 'animate-spin' : ''}`} />
            {t('preloadBanner.retry', { defaultValue: 'Νέα δοκιμή' })}
          </button>
          <button
            onClick={() => setConfirmCash(true)}
            disabled={submitting}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-700/40 hover:bg-emerald-700/60 text-white text-xs font-semibold transition-all active:scale-95 disabled:opacity-50"
          >
            <Banknote className="h-3.5 w-3.5" />
            {t('preloadBanner.switchToCash', { defaultValue: 'Μετρητά' })}
          </button>
        </div>
      )}

      {/* Switch-to-cash confirmation modal */}
      {confirmCash && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !submitting && setConfirmCash(false)}
        >
          <div
            className="w-full max-w-md bg-surface-2 dark:bg-surface-1 border border-line rounded-2xl shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-5 border-b border-line">
              <h3 className="text-lg font-bold text-t1 flex items-center gap-2">
                <Banknote className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                {t('preloadBanner.confirmCashTitle', { defaultValue: 'Αλλαγή σε μετρητά;' })}
              </h3>
            </div>
            <div className="p-5 text-sm text-t2 space-y-2">
              <p>{t('preloadBanner.confirmCashBody', {
                defaultValue: 'Η συναλλαγή με κάρτα ακυρώνεται. Ο διανομέας θα εισπράξει το ποσό σε μετρητά κατά την παράδοση.',
              })}</p>
              <p className="text-xs text-t4">{t('preloadBanner.confirmCashHint', {
                defaultValue: 'Δεν μπορεί να αναιρεθεί από το ίδιο σημείο — απαιτεί χειροκίνητη ενέργεια στη βάση.',
              })}</p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-line">
              <button
                onClick={() => setConfirmCash(false)}
                disabled={submitting}
                className="px-4 py-2 rounded-lg bg-surface-2 hover:bg-surface-3 text-t2 font-medium text-sm disabled:opacity-50"
              >
                {t('common:cancel', { defaultValue: 'Άκυρο' })}
              </button>
              <button
                onClick={handleSwitchToCash}
                disabled={submitting}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-sm disabled:opacity-50 inline-flex items-center gap-2"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {t('preloadBanner.confirmCashOk', { defaultValue: 'Ναι, μετρητά' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrderPreloadBanner;
